import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import lscache from 'lscache';
import { useJourneys } from 'src/store/journeys';
import { useCurrentOrganization } from 'src/store/organization';
import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import { Button } from 'src/common/button';
import useDashboardStore from 'src/dashboard/store/dashboard';
import { trackSegmentEvent } from 'src/utils/segment';
import { useClickAway } from 'react-use';
import { functionNoop } from 'src/utils/function/noop';
import {
  CreateJourneyMethodType,
  CreateJourneyMethodTypes,
  CreateJourneyWidget,
} from 'src/dashboard/create-journey/widget';
import queryString from 'query-string';
import classNames from 'classnames';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';
import { useHistory } from 'react-router-dom';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

const HeaderCreateJourneyButton = () => {
  const history = useHistory();
  // TODO update show requirements for hint
  const hasJourneyOnOrg = useJourneys((state) => state.journeys.length > 0);
  const journeysLoaded = useJourneys((state) => state.journeysLoaded);
  const hintWillBeShown = useMemo(
    () => !hasJourneyOnOrg && !lscache.get('create-first-journey-hint') && journeysLoaded,
    [hasJourneyOnOrg, journeysLoaded]
  );
  const { create } = queryString.parse(window.location.search) as Record<'create', CreateJourneyMethodType>;

  const setJourneyWorkspace = useNewJourneyStore((state) => state.setJourneyWorkspace);
  const setIsCreatingJourney = useDashboardStore((state) => state.setIsCreatingJourney);

  const isFreeOrg = useCurrentOrganization((state) => state.isFreeOrg);
  const hasReachedFreeLimit = useJourneys((state) => state.hasReachedFreeLimit);
  const isStarter = useCurrentSubscription((state) => state.isStarter)
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);
  const { checkFeature, showUpgradeModal } = useFeatureRestrictions();

  const elementRef = useRef<HTMLDivElement>(null);

  const [showCreateJourneyHint, setShowCreateJourneyHint] = useState(hintWillBeShown);

  const createJourneyModalOpenRef = useRef(functionNoop);
  const createJourneyModalCloseRef = useRef(functionNoop);

  const createJourneyPanelOpenRef = useRef(functionNoop);
  const createJourneyPanelCloseRef = useRef(functionNoop);

  useEffect(() => {
    if (create) {
      // Check journey limit for template creation too
      if (create === 'template') {
        if (!checkFeature('journey_creation')) {
          showUpgradeModal('journey_creation', 'You have reached your journey limit. Upgrade to create more journeys from templates.');
          history.replace({
            pathname: `${history.location.pathname}`,
          });
          return;
        }
        if (CreateJourneyMethodTypes.includes(create)) {
          createJourneyPanelOpenRef.current(create);
        }
        return;
      }
      
      // Check if user can create journeys (including journey limit check)
      if (!checkFeature('journey_creation')) {
        showUpgradeModal('journey_creation', 'You have reached your journey limit. Upgrade to create more journeys.');
        history.replace({
          pathname: `${history.location.pathname}`,
        });
        return;
      }
      
      if (CreateJourneyMethodTypes.includes(create)) {
        createJourneyPanelOpenRef.current(create);
      }
    }
  }, [create, checkFeature, showUpgradeModal, history]);

  useClickAway(elementRef, () => {
    createJourneyModalCloseRef.current();
  });

  const { anchorRef, anchorStyle, renderSpotlight } = useSpotlight({ placement: 'right', boundaryPadding: 32 });

  const onDissmissCreateJourneyHint = () => {
    setShowCreateJourneyHint(false);
    // TODO update this to track org id as well
    lscache.set('create-first-journey-hint', true);
  };

  useEffect(() => {
    if (hintWillBeShown) {
      setShowCreateJourneyHint(true);
    }
  }, [hintWillBeShown]);

  const onCreateJourneyClick = () => {
    // Check if user can create journeys (including journey limit check)
    if (!checkFeature('journey_creation')) {
      showUpgradeModal('journey_creation', 'You have reached your journey limit. Upgrade to create more journeys.');
      return;
    }
    const { currentOrganization, currentWorkspace, teamWorkspace } = useCurrentOrganization.getState();
    setJourneyWorkspace(currentWorkspace && currentWorkspace.id ? currentWorkspace : teamWorkspace);
    setShowCreateJourneyHint(false);
    const skipFeatureFlag = true;
    // if (true) {
    if (skipFeatureFlag || currentOrganization.has_access_to_neue_only) {
      onCreateNeueJourneyClick();
    } else {
      setIsCreatingJourney(true);
    }
    trackSegmentEvent('Create Journey Cta Clicked', {});
  };

  const onCreateNeueJourneyClick = () => {
    createJourneyModalOpenRef.current();
  };

  return (
    <div ref={elementRef} className='relative'>
      <Button
        ref={anchorRef}
        label='Create Journey'
        size='regular'
        variant='primary'
        className={classNames('shrink-0 w-full create-journey-button journey-content-editor', {
          'opacity-80': !checkFeature('journey_creation'),
        })}
        style={anchorStyle}
        onClick={onCreateJourneyClick}
      />
      {showCreateJourneyHint &&
        renderSpotlight({
          content: <span>Get started by creating your first Journey</span>,
          showDismissButton: true,
          show: hintWillBeShown,
          onDismiss: onDissmissCreateJourneyHint,
        })}
      <CreateJourneyWidget
        panelCloseRef={createJourneyPanelCloseRef}
        panelOpenRef={createJourneyPanelOpenRef}
        modalOpenRef={createJourneyModalOpenRef}
        modalCloseRef={createJourneyModalCloseRef}
      />
    </div>
  );
};

export default HeaderCreateJourneyButton;
